import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useHomeGeo } from "@/hooks/useHomeGeo";

const HomeFeedsContext = createContext(null);

const CACHE_MS = 45_000;
let memoryCache = { key: "", at: 0, data: null };

export function HomeFeedsProvider({ children, limit = 8 }) {
  const { coords, ready: geoReady } = useHomeGeo();
  const [data, setData] = useState(memoryCache.data);
  const [loading, setLoading] = useState(!memoryCache.data);
  const [error, setError] = useState(false);
  const mounted = useRef(true);

  const cacheKey = useMemo(
    () => `${limit}:${coords?.lat?.toFixed(3) || ""}:${coords?.lng?.toFixed(3) || ""}`,
    [limit, coords?.lat, coords?.lng],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!geoReady) return;

    const now = Date.now();
    if (memoryCache.key === cacheKey && memoryCache.data && now - memoryCache.at < CACHE_MS) {
      setData(memoryCache.data);
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    const params = { limit };
    if (coords?.lat != null && coords?.lng != null) {
      params.lat = coords.lat;
      params.lng = coords.lng;
    }

    api
      .get("/home/feeds", { params })
      .then((r) => {
        if (cancelled || !mounted.current) return;
        memoryCache = { key: cacheKey, at: Date.now(), data: r.data };
        setData(r.data);
      })
      .catch(() => {
        if (cancelled || !mounted.current) return;
        setError(true);
        setData(null);
      })
      .finally(() => {
        if (!cancelled && mounted.current) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [geoReady, cacheKey, limit, coords?.lat, coords?.lng]);

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      coords,
      geoReady,
    }),
    [data, loading, error, coords, geoReady],
  );

  return <HomeFeedsContext.Provider value={value}>{children}</HomeFeedsContext.Provider>;
}

export function useHomeFeeds() {
  const ctx = useContext(HomeFeedsContext);
  if (!ctx) {
    throw new Error("useHomeFeeds must be used within HomeFeedsProvider");
  }
  return ctx;
}
